// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/invitationMusic.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/invitationMusic.js
//
// "Invitation music": one YouTube video the host picks, played in a card at
// the end of the invitation - the song for the occasion, the way a story or
// a post carries one.
//
// ── Within YouTube's terms ─────────────────────────────────────────────────
//
// It plays in YouTube's own embedded player, never as hidden audio. YouTube's
// Required Minimum Functionality for embedded players
// (developers.google.com/youtube/terms/required-minimum-functionality):
//
//   - the player's viewport is at least 200 x 200 px          (MIN_PLAYER_PX)
//   - nothing is drawn over any part of it, controls included  (our Play /
//     Pause sits beside the player, not on it)
//   - it does not autoplay until it is visible, more than half of it on
//     screen                                                   (AUTOPLAY_VISIBLE)
//   - at most one autoplaying YouTube player per page
//
// So the card plays when the guest scrolls to it, not the moment the page
// opens - which is also why it is the last section: the details come first.
//
// ── Sound ──────────────────────────────────────────────────────────────────
//
// Audible or not at all - never a silent, muted start. Browsers refuse to
// start sound before the visitor has touched the page, so the website waits
// ("Tap anywhere to play") and starts it, with sound, on their first tap. The
// app's web view may start with sound by itself, except on an iPhone in Low
// Power Mode, which refuses web video autoplay altogether - there the guest
// presses play on the video.
//
// Pure: no imports, no I/O. Searching goes through YouTube's Data API
// (search.list) - the website's /api/youtube/search proxies it with a cache,
// the app calls it the same way.

/** The feature, on or off. */
export const INVITATION_MUSIC_ENABLED = true;

/** YouTube's minimum player viewport, in px, on each side. */
export const MIN_PLAYER_PX = 200;

/** How much of the player must be on screen before it may start by itself. */
export const AUTOPLAY_VISIBLE = 0.5;

/**
 * Start the song as the invitation opens, wherever the player is on the page
 * - the host's choice, so the first thing a guest gets is the music.
 *
 * NOTE: YouTube's Required Minimum Functionality says an embedded player must
 * not start automatically until more than half of it is visible. With the
 * player as the last section, starting on load does not meet that, and puts
 * the API key at risk if YouTube reviews it. false restores the compliant
 * behaviour everywhere: start once the guest scrolls to it (AUTOPLAY_VISIBLE).
 */
export const AUTOPLAY_ON_LOAD = true;

/** The name guests and hosts see. */
export const INVITATION_MUSIC_LABEL = 'Invitation music';

const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

/** A YouTube video id, or null. */
export function cleanVideoId(value) {
  const v = String(value ?? '').trim();
  return VIDEO_ID.test(v) ? v : null;
}

/**
 * The saved shape (events.invitation_music), from whatever the column holds.
 * Null when there is no usable video.
 *
 * @returns {null | { provider: 'youtube', videoId: string, title: string,
 *   channel: string|null, thumbnail: string|null }}
 */
export function parseInvitationMusic(value) {
  let v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return null; }
  }
  if (!v || typeof v !== 'object') return null;
  const videoId = cleanVideoId(v.videoId ?? v.video_id);
  if (!videoId) return null;
  return {
    provider: 'youtube',
    videoId,
    title: String(v.title || '').slice(0, 200) || 'Untitled video',
    channel: v.channel ? String(v.channel).slice(0, 120) : null,
    thumbnail: typeof v.thumbnail === 'string' ? v.thumbnail : null,
  };
}

/** HTML entities YouTube leaves in snippet titles ("Don&#39;t Stop"). */
function decodeEntities(text) {
  return String(text ?? '')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/**
 * search.list parameters for a query: embeddable videos only, so a picked
 * song never turns out unplayable in the card. 100 quota units a call.
 */
export function musicSearchParams(query, maxResults = 12) {
  return {
    part: 'snippet',
    type: 'video',
    videoEmbeddable: 'true',
    videoSyndicated: 'true',
    safeSearch: 'moderate',
    maxResults: String(maxResults),
    q: String(query ?? '').trim().slice(0, 100),
  };
}

/**
 * A search.list response as picker rows: [{ videoId, title, channel, thumbnail }].
 * Accepts the raw API response or the website proxy's (which passes it on).
 */
export function parseMusicSearch(json) {
  const items = Array.isArray(json?.items) ? json.items : [];
  return items
    .map((item) => {
      const videoId = cleanVideoId(item?.id?.videoId ?? item?.videoId);
      if (!videoId) return null;
      const s = item.snippet || {};
      return {
        videoId,
        title: decodeEntities(s.title || item.title) || 'Untitled video',
        channel: decodeEntities(s.channelTitle || item.channel) || null,
        thumbnail: s.thumbnails?.medium?.url || s.thumbnails?.high?.url || s.thumbnails?.default?.url || item.thumbnail || null,
      };
    })
    .filter(Boolean);
}

/** The saved shape for a picked search row. */
export function toInvitationMusic(row) {
  return parseInvitationMusic({ ...row, provider: 'youtube' });
}

/**
 * The embed address. Autoplay starts muted where the platform requires it;
 * loop needs the video named again as its own playlist.
 */
export function youtubeEmbedUrl(videoId, { autoplay = false, mute = false, origin = null } = {}) {
  const id = cleanVideoId(videoId);
  if (!id) return null;
  const params = new URLSearchParams({
    enablejsapi: '1',
    playsinline: '1',
    rel: '0',
    loop: '1',
    playlist: id,
    autoplay: autoplay ? '1' : '0',
    mute: mute ? '1' : '0',
  });
  if (origin) params.set('origin', origin);
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

/** The player's height for a width: 16:9, never under YouTube's minimum. */
export function playerHeightFor(width) {
  return Math.max(MIN_PLAYER_PX, Math.round((Number(width) || 0) * 9 / 16));
}
